<template>
  <div class="pl-shell">
    <!-- HERO -->
    <section class="pl-hero">
      <div>
        <p class="eyebrow">LotLedger Wine Bar</p>
        <h1>Profit & Loss</h1>
        <p class="hero-copy">
          See revenue, carrying cost, expenses, and net profit for the selected period.
        </p>
      </div>

      <button @click="fetchReport" class="primary-action">
        Generate Report
      </button>
    </section>

    <!-- FILTERS -->
    <section class="pl-toolbar">
      <label>
        <span>Start Date</span>
        <input type="date" v-model="startDate" />
      </label>

      <label>
        <span>End Date</span>
        <input type="date" v-model="endDate" />
      </label>

      <button type="button" @click="setToday" class="ghost-action">
        Today
      </button>

      <button type="button" @click="setThisMonth" class="ghost-action">
        This Month
      </button>
    </section>

    <!-- SUMMARY CARDS -->
    <section class="pl-summary-grid">
      <article class="summary-card">
        <span>Total Revenue</span>
        <strong>${{ money(reportData.total_revenue) }}</strong>
      </article>

      <article class="summary-card">
        <span>Gross Profit</span>
        <strong>${{ money(grossProfit) }}</strong>
      </article>

      <article class="summary-card">
        <span>Net Profit</span>
        <strong :class="{ negative: netProfit < 0 }">
          ${{ money(netProfit) }}
        </strong>
      </article>

      <article class="summary-card">
        <span>Net Profit %</span>
        <strong :class="{ negative: netProfitPercent < 0 }">
          {{ percent(netProfitPercent) }}
        </strong>
      </article>
    </section>

    <!-- MAIN REPORT -->
    <section class="report-grid">
      <!-- P&L STATEMENT -->
      <article class="statement-card">
        <div class="section-heading">
          <p class="eyebrow">Statement</p>
          <h2>Operating Results</h2>
        </div>

        <div class="statement-list">
          <div class="statement-row major">
            <span>Gross Revenue</span>
            <strong>${{ money(reportData.gross_revenue || reportData.total_revenue) }}</strong>
          </div>

          <div class="statement-row child">
            <span>Refunds / Credits</span>
            <strong class="negative">
              -${{ money(reportData.total_refunds) }}
            </strong>
          </div>

          <div class="statement-row subtotal">
            <span>Net Revenue</span>
            <strong>${{ money(reportData.total_revenue) }}</strong>
          </div>

          <div class="statement-row">
            <span>Cost of Goods Sold</span>
            <strong>${{ money(reportData.total_cogs) }}</strong>
          </div>

          <div class="statement-row subtotal">
            <span>Gross Profit</span>
            <strong>${{ money(grossProfit) }}</strong>
          </div>

          <div class="statement-row muted">
            <span>Gross Profit %</span>
            <strong>{{ percent(grossProfitPercent) }}</strong>
          </div>

          <div class="statement-divider"></div>

          <div class="statement-row section-row">
            <span>Selling Expenses</span>
            <strong>${{ money(totalSellingExpenses) }}</strong>
          </div>

          <div class="statement-row child">
            <span>Freight Out</span>
            <strong>${{ money(reportData.total_freight) }}</strong>
          </div>

          <div class="statement-row child">
            <span>Sales Taxes</span>
            <strong>${{ money(reportData.total_taxes) }}</strong>
          </div>

          <div class="statement-row child">
            <span>Fees</span>
            <strong>${{ money(reportData.total_fees) }}</strong>
          </div>

          <div class="statement-divider"></div>

          <div class="statement-row">
            <span>Operating Expenses</span>
            <strong>${{ money(reportData.total_expenses) }}</strong>
          </div>

          <div
            v-for="expense in reportData.expense_categories"
            :key="expense.category_name"
            class="statement-row child"
          >
            <span>{{ expense.category_name }}</span>
            <strong>${{ money(expense.total_cost) }}</strong>
          </div>

          <div class="statement-row">
            <span>Inventory Adjustments</span>
            <strong :class="{ negative: Number(reportData.total_inventory_adjustments || 0) < 0 }">
              ${{ money(reportData.total_inventory_adjustments) }}
            </strong>
          </div>

          <div class="statement-row">
            <span>Cash Over / Short</span>
            <strong :class="{ negative: Number(reportData.cash_over_short || 0) < 0 }">
              {{ signedMoney(reportData.cash_over_short) }}
            </strong>
          </div>

          <div
            v-for="financial in otherFinancialCategories"
            :key="financial.category"
            class="statement-row child"
          >
            <span>{{ formatFinancialCategory(financial.category) }}</span>
            <strong :class="{ negative: Number(financial.total_amount || 0) < 0 }">
              {{ signedMoney(financial.total_amount) }}
            </strong>
          </div>

          <div class="statement-divider"></div>

          <div class="statement-row final">
            <span>Net Profit</span>
            <strong :class="{ negative: netProfit < 0 }">
              ${{ money(netProfit) }}
            </strong>
          </div>

          <div class="statement-row muted">
            <span>Net Profit %</span>
            <strong :class="{ negative: netProfitPercent < 0 }">
              {{ percent(netProfitPercent) }}
            </strong>
          </div>
        </div>
      </article>

      <!-- SIDE INSIGHTS -->
      <article class="insight-card">
        <div class="section-heading">
          <p class="eyebrow">Readout</p>
          <h2>Quick Take</h2>
        </div>

        <div class="metric-stack">
          <div class="metric-pill">
            <span>COGS %</span>
            <strong>{{ percent(cogsPercent) }}</strong>
          </div>

          <div class="metric-pill">
            <span>Expense %</span>
            <strong>{{ percent(expensePercent) }}</strong>
          </div>

          <div class="metric-pill">
            <span>Selling Expense %</span>
            <strong>{{ percent(sellingExpensePercent) }}</strong>
          </div>
        </div>

        <div class="metric-pill">
          <span>Cash Over / Short</span>
          <strong :class="{ negative: Number(reportData.cash_over_short || 0) < 0 }">
            {{ signedMoney(reportData.cash_over_short) }}
          </strong>
        </div>

        <div class="insight-note">
          <h3>{{ profitMessage.title }}</h3>
          <p>{{ profitMessage.body }}</p>
        </div>
      </article>
    </section>

    <section v-if="!hasReportData" class="empty-state">
      <p class="eyebrow">No Report Yet</p>
      <h2>Choose a date range and generate the report.</h2>
      <p>The report will summarize sales, COGS, expenses, and net profit.</p>
    </section>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'ProfitLossReport',

  data() {
    return {
      startDate: '',
      endDate: '',
      reportData: this.blankReport()
    };
  },

  computed: {
    hasReportData() {
      return (
        Number(this.reportData.gross_revenue || 0) !== 0 ||
        Number(this.reportData.total_revenue || 0) !== 0 ||
        Number(this.reportData.total_cogs || 0) !== 0 ||
        Number(this.reportData.total_expenses || 0) !== 0 ||
        Number(this.reportData.total_financial_transactions || 0) !== 0 ||
        Number(this.reportData.net_profit || 0) !== 0
      );
    },

    grossProfit() {
      return Number(this.reportData.total_revenue || 0) -
        Number(this.reportData.total_cogs || 0);
    },

    grossProfitPercent() {
      const revenue = Number(this.reportData.total_revenue || 0);
      return revenue > 0 ? (this.grossProfit / revenue) * 100 : null;
    },

    totalSellingExpenses() {
      return Number(this.reportData.total_freight || 0) +
        Number(this.reportData.total_taxes || 0) +
        Number(this.reportData.total_fees || 0);
    },

    otherFinancialCategories() {
      return (this.reportData.financial_categories || []).filter(row => {
        return row.category !== 'cash_over_short';
      });
    },

    netProfit() {
      if (this.reportData.net_profit !== null && this.reportData.net_profit !== undefined) {
        return Number(this.reportData.net_profit || 0);
      }

      return Number(this.reportData.total_revenue || 0) -
        Number(this.reportData.total_cogs || 0) -
        this.totalSellingExpenses -
        Number(this.reportData.total_expenses || 0) +
        Number(this.reportData.total_inventory_adjustments || 0) +
        Number(this.reportData.total_financial_transactions || 0);
    },

    netProfitPercent() {
      const revenue = Number(this.reportData.total_revenue || 0);
      return revenue > 0 ? (this.netProfit / revenue) * 100 : null;
    },

    cogsPercent() {
      const revenue = Number(this.reportData.total_revenue || 0);
      return revenue > 0
        ? (Number(this.reportData.total_cogs || 0) / revenue) * 100
        : null;
    },

    expensePercent() {
      const revenue = Number(this.reportData.total_revenue || 0);
      return revenue > 0
        ? (Number(this.reportData.total_expenses || 0) / revenue) * 100
        : null;
    },

    sellingExpensePercent() {
      const revenue = Number(this.reportData.total_revenue || 0);
      return revenue > 0
        ? (this.totalSellingExpenses / revenue) * 100
        : null;
    },

    profitMessage() {
      if (!this.hasReportData) {
        return {
          title: 'Waiting on numbers',
          body: 'Generate the report to see the period summary.'
        };
      }

      if (this.netProfit < 0) {
        return {
          title: 'Loss period',
          body: 'Net profit is negative for this range. Check COGS, expenses, and any inventory adjustments.'
        };
      }

      if (this.netProfitPercent >= 25) {
        return {
          title: 'Strong period',
          body: 'Net margin is healthy. This range looks profitable after costs and expenses.'
        };
      }

      return {
        title: 'Profitable, but watch costs',
        body: 'The period is profitable, but margins may need monitoring as sales volume grows.'
      };
    }
  },

  methods: {
    blankReport() {
      return {
        gross_revenue: 0,
        total_refunds: 0,
        total_revenue: 0,

        total_cogs: 0,
        total_freight: 0,
        total_taxes: 0,
        total_fees: 0,
        total_selling_expenses: 0,

        total_expenses: 0,
        total_inventory_adjustments: 0,

        total_financial_transactions: 0,
        cash_over_short: 0,
        financial_categories: [],

        net_profit: 0,

        expense_categories: [],
        transactions: []
      };
    },

    async fetchReport() {
      try {
        const response = await api.get('/reports/profit-loss', {
          params: {
            start_date: this.startDate || null,
            end_date: this.endDate || null
          }
        });

        this.reportData = {
          ...this.blankReport(),
          ...(response.data || {}),
          expense_categories: response.data?.expense_categories || [],
          financial_categories: response.data?.financial_categories || [],
          transactions: response.data?.transactions || []
        };
      } catch (error) {
        console.error('Error fetching P&L report:', error);
        alert('Failed to fetch Profit & Loss report. Check the backend logs.');
      }
    },

    setToday() {
      const today = new Date().toISOString().split('T')[0];
      this.startDate = today;
      this.endDate = today;
      this.fetchReport();
    },

    setThisMonth() {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1)
        .toISOString()
        .split('T')[0];

      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        .toISOString()
        .split('T')[0];

      this.startDate = start;
      this.endDate = end;
      this.fetchReport();
    },

    money(value) {
      return Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    signedMoney(value) {
      const amount = Number(value || 0);
      const prefix = amount > 0 ? '+' : amount < 0 ? '-' : '';

      return `${prefix}$${this.money(Math.abs(amount))}`;
    },

    formatFinancialCategory(category) {
      const labels = {
        cash_over_short: 'Cash Over / Short'
      };

      return labels[category] || String(category || 'Financial Transaction')
        .replaceAll('_', ' ')
        .replace(/\b\w/g, letter => letter.toUpperCase());
    },

    percent(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return 'N/A';
      }

      return `${Number(value).toFixed(2)}%`;
    }
  },

  mounted() {
    this.setToday();
  }
};
</script>

<style scoped>
.pl-shell {
  min-height: 100vh;
  padding: 28px;
  color: #f7efe8;
  font-family: "Inter", "Poppins", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.38), transparent 34%),
    radial-gradient(circle at bottom right, rgba(194, 74, 47, 0.18), transparent 32%),
    linear-gradient(135deg, #0d090d 0%, #171016 42%, #24141b 100%);
}

.pl-hero,
.pl-toolbar,
.summary-card,
.statement-card,
.insight-card,
.empty-state {
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.085), rgba(255, 255, 255, 0.035));
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.34);
  backdrop-filter: blur(18px);
}

.pl-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 22px;
  padding: 30px;
}

.eyebrow {
  margin: 0 0 8px;
  color: #c8a66a;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 12px;
  font-weight: 900;
}

.pl-hero h1 {
  margin: 0;
  font-size: clamp(42px, 7vw, 78px);
  line-height: 0.92;
  letter-spacing: -0.07em;
}

.hero-copy {
  max-width: 660px;
  margin: 16px 0 0;
  color: #cfc2bd;
  font-size: 16px;
  line-height: 1.5;
}

.pl-toolbar {
  display: grid;
  grid-template-columns: 1fr 1fr auto auto;
  gap: 12px;
  align-items: end;
  margin-top: 18px;
  padding: 18px;
}

.pl-toolbar label {
  display: grid;
  gap: 8px;
}

.pl-toolbar label span {
  color: #b9aaa4;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.pl-shell input {
  min-height: 52px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  outline: 0;
  background: rgba(6, 4, 6, 0.58);
  color: #fff8f1;
  padding: 0 14px;
  font-size: 15px;
  font-family: inherit;
}

.pl-shell input:focus {
  border-color: rgba(200, 166, 106, 0.65);
  box-shadow: 0 0 0 3px rgba(200, 166, 106, 0.14);
}

.pl-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(170px, 1fr));
  gap: 14px;
  margin-top: 18px;
}

.summary-card {
  padding: 20px;
}

.summary-card span {
  display: block;
  color: #a99d98;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.summary-card strong {
  display: block;
  margin-top: 10px;
  font-size: 34px;
  line-height: 1;
  letter-spacing: -0.05em;
}

.negative {
  color: #ff9f8e !important;
}

.report-grid {
  display: grid;
  grid-template-columns: minmax(420px, 1.2fr) minmax(280px, 0.8fr);
  gap: 18px;
  margin-top: 18px;
}

.statement-card,
.insight-card {
  padding: 24px;
}

.section-heading h2 {
  margin: 0;
  font-size: clamp(30px, 4vw, 48px);
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.statement-list {
  display: grid;
  gap: 8px;
  margin-top: 20px;
}

.statement-row {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: center;
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.065);
}

.statement-row span {
  color: #d8cbc5;
}

.statement-row strong {
  color: #fff8f1;
  font-size: 16px;
  white-space: nowrap;
}

.statement-row.major {
  background: rgba(200, 166, 106, 0.12);
  border-color: rgba(200, 166, 106, 0.2);
}

.statement-row.subtotal,
.statement-row.final {
  background: rgba(143, 18, 63, 0.26);
  border-color: rgba(194, 74, 47, 0.26);
}

.statement-row.final strong {
  font-size: 24px;
  color: #f1c979;
}

.statement-row.muted {
  background: rgba(255, 255, 255, 0.045);
}

.statement-row.section-row {
  background: rgba(255, 255, 255, 0.07);
}

.statement-row.child {
  margin-left: 20px;
}

.statement-row.child span::before {
  content: "— ";
  color: #c8a66a;
}

.statement-divider {
  height: 1px;
  margin: 6px 0;
  background: rgba(255, 255, 255, 0.1);
}

.metric-stack {
  display: grid;
  gap: 12px;
  margin-top: 20px;
}

.metric-pill {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  padding: 16px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.24);
  border: 1px solid rgba(255, 255, 255, 0.075);
}

.metric-pill span {
  color: #a99d98;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.metric-pill strong {
  color: #f1c979;
  font-size: 20px;
}

.insight-note {
  margin-top: 18px;
  padding: 20px;
  border-radius: 24px;
  background:
    radial-gradient(circle at top left, rgba(200, 166, 106, 0.18), transparent 40%),
    rgba(0, 0, 0, 0.23);
  border: 1px solid rgba(200, 166, 106, 0.14);
}

.insight-note h3 {
  margin: 0;
  font-size: 24px;
  letter-spacing: -0.04em;
}

.insight-note p {
  margin: 10px 0 0;
  color: #cfc2bd;
  line-height: 1.5;
}

.empty-state {
  margin-top: 18px;
  padding: 46px 30px;
  text-align: center;
}

.empty-state h2 {
  margin: 0;
  font-size: clamp(32px, 5vw, 54px);
  letter-spacing: -0.06em;
}

.empty-state p {
  color: #cfc2bd;
}

.primary-action,
.ghost-action {
  min-height: 50px;
  padding: 0 18px;
  border-radius: 999px;
  border: 0;
  color: #fff8f1;
  font-weight: 900;
  font-size: 14px;
  cursor: pointer;
  transition: transform 0.14s ease, box-shadow 0.14s ease, background 0.14s ease;
}

.primary-action {
  background: linear-gradient(135deg, #8f123f, #c24a2f);
  box-shadow: 0 14px 32px rgba(194, 74, 47, 0.3);
}

.ghost-action {
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.11);
}

.primary-action:hover,
.ghost-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.24);
}

/* ---------- Responsive ---------- */

@media (max-width: 1100px) {
  .pl-summary-grid,
  .report-grid {
    grid-template-columns: 1fr 1fr;
  }

  .report-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .pl-shell {
    padding: 16px;
  }

  .pl-hero {
    flex-direction: column;
    align-items: stretch;
  }

  .pl-toolbar,
  .pl-summary-grid {
    grid-template-columns: 1fr;
  }

  .statement-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .statement-row.child {
    margin-left: 0;
  }

  .primary-action,
  .ghost-action {
    width: 100%;
  }
}
</style>